/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import * as drive from "../../../../api/drive";
import { downloadFile } from "../useDrive";

/**
 * Opens the file in-place instead of forcing a download first, the way
 * WorkDrive's preview pane does. Only image/video/audio/pdf get an inline
 * renderer — everything else (spreadsheets, archives, unknown types) falls
 * back to "Download instead" rather than pretending the browser can show it.
 */
const FilePreviewDialog = ({ open, file, onClose }) => {
  const [state, setState] = useState({ loading: true, url: null, contentType: null, error: null });

  useEffect(() => {
    if (!open || !file) return;
    setState({ loading: true, url: null, contentType: null, error: null });
    Promise.all([drive.getFile(file.id), drive.getPreviewUrl(file.id)])
      .then(([{ file: fileRow }, target]) => {
        setState({ loading: false, url: target.downloadUrl, contentType: fileRow.content_type, error: null });
      })
      .catch((err) => {
        setState({ loading: false, url: null, contentType: null, error: err.message || "Could not open preview." });
      });
  }, [open, file]);

  const kind = (state.contentType || "").split("/")[0];
  const isPdf = state.contentType === "application/pdf";
  const renderable = !state.loading && !state.error && state.url && (isPdf || ["image", "video", "audio"].includes(kind));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" slotProps={{ paper: { sx: { height: "80vh" } } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 600 }} noWrap>{file?.name}</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", p: renderable ? 0 : 3 }}>
        {state.loading && <CircularProgress sx={{ color: "accent.main" }} />}
        {state.error && <Alert severity="error">{state.error}</Alert>}

        {!state.loading && !state.error && renderable && (
          <>
            {kind === "image" && (
              <img src={state.url} alt={file?.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            )}
            {kind === "video" && (
              <video src={state.url} controls style={{ maxWidth: "100%", maxHeight: "100%" }} />
            )}
            {kind === "audio" && <audio src={state.url} controls style={{ width: "80%" }} />}
            {isPdf && <iframe src={state.url} title={file?.name} style={{ width: "100%", height: "100%", border: "none" }} />}
          </>
        )}

        {!state.loading && !state.error && !renderable && (
          <Box sx={{ textAlign: "center" }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1 }} />
            <Typography sx={{ fontSize: 13.5, color: "secondaryText.color" }}>
              No preview available for this file type.
            </Typography>
          </Box>
        )}
      </Box>

      <DialogActions>
        <Button onClick={onClose} sx={{ color: "text.color", textTransform: "none" }}>Close</Button>
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          sx={{ backgroundColor: "accent.solid", color: "#fff", textTransform: "none" }}
          onClick={() => downloadFile(file.id, file.name)}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FilePreviewDialog;
